import React, {
   CSSProperties,
   Dispatch,
   ReactElement,
   SetStateAction, useMemo,
   useState
} from 'react';
import { BorderPanel } from '../../components/layout/borderpanel';
import { TabBar, TabBarButtonProps } from '../../components/controls/tabbar';
import { CardPanel, CardPanelItem } from '../../components/layout/cardpanel';
import {reactNodeIsOfType} from '../../utils';

export interface TabBarPanelProps {
   /**
    * Sets the default active tab id
    */
   defaultTabId?: string;

   /**
    * Sets a className.
    */
   className?: string;

   /**
    * Sets styles.
    */
   style?: CSSProperties;
}

/**
 * {@docCategory TabBarPanel}
 */
export interface TabBarPanelItemProps {
   /**
    * The id of the tab item.
    */
   id: string;

   /**
    * The title to display on the tab item.
    */
   title: string;

   /**
    * The icon to display on the tab item.
    */
   icon: ReactElement;

   /**
    * The badge to display on the tab item. Can be a number, string, or 'dot'.
    * 'dot' will display a small dot on the tab item button.
    */
   badge?: number | string | 'dot';
}

export const TabBarPanel = (props: React.PropsWithChildren<TabBarPanelProps>) => {

   const children: ReactElement[] = useMemo(()=>(React.Children.toArray(props.children).filter(child => {
      return React.isValidElement(child) && reactNodeIsOfType(child as ReactElement, TabBarPanelItem);
   }) as ReactElement[]),[props.children]);

   const [activePageId, setActivePageId] = useState<string>(props.defaultTabId !== undefined ? props.defaultTabId : getDefaultTabId(children));

   const tabBar = useMemo(()=>(
      <TabBar
         buttons={tabsToButtons((children as any) as TabBarPanelItem[])}
         activeButtonId={activePageId}
         onClick={tabBarButtonClicked(setActivePageId)}
      />
   ),[children, activePageId]);

   return (
      <BorderPanel
         className={props.className}
         style={props.style}
         bottom={tabBar}
      >
         <CardPanel activeCardId={activePageId}>
            {React.Children.map(props.children, (child => {
               const childElement = child as ReactElement;
               if (reactNodeIsOfType(childElement, TabBarPanelItem)) {
                  return (
                     <CardPanelItem id={childElement.props.id}>
                        {childElement.props.children}
                     </CardPanelItem>
                  );
               }
            }))}
         </CardPanel>
      </BorderPanel>
   );
};

export class TabBarPanelItem extends React.Component<React.PropsWithChildren<TabBarPanelItemProps>> {
}

const getDefaultTabId = (children: ReactElement[]): string => {

   return (children.length > 0) ? children[0].props.id : '';
};

const tabBarButtonClicked = (setActivePageId: Dispatch<SetStateAction<string>>) => (id: string) => {
   setActivePageId(id);
};

const tabsToButtons = (children: TabBarPanelItem[]): TabBarButtonProps[] => {
   return children.map(childElement => {
      return childElement.props;
   });
};
